import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import {
  FiHome, FiInbox, FiPlusCircle, FiUsers, FiFolder, FiPackage,
  FiBell, FiMonitor, FiHardDrive, FiSettings, FiGlobe, FiTool,
  FiAlertCircle, FiLogOut, FiChevronRight, FiCloud,
} from 'react-icons/fi';
import Notificaciones from './Notificaciones';
import FotoPerfil from './ui/FotoPerfil';
import BASE_URL from '../config/config';
import { cabecerasAuth, claves } from '../queries/cliente';
import '../css/sidebar.css';
import { DEPTO_LABELS_CORTO } from '../constants/usuarios';

// Navegación por rol: secciones con rótulo + lista plana de enlaces (sin
// acordeones). `clave` es la clave i18n del rótulo del enlace o de la sección.
export const buildNav = (role) => {
  const miEspacio = {
    id: 'mi-espacio',
    clave: 'menu.seccionMiEspacio',
    items: [
      { clave: 'menu.adhocDrive', path: '/dashboard/adhoc-drive', icon: FiCloud },
      { clave: 'menu.misServicios', path: '/dashboard/servicios', icon: FiTool },
    ],
  };

  if (role === 'admin') {
    return [
      {
        id: 'general',
        clave: 'menu.seccionGeneral',
        items: [
          { clave: 'menu.panel', path: '/dashboard', icon: FiHome },
        ],
      },
      {
        id: 'gestion',
        clave: 'menu.seccionGestion',
        items: [
          { clave: 'menu.tickets', path: '/dashboard/tickets', icon: FiInbox },
          { clave: 'menu.usuarios', path: '/dashboard/usuarios', icon: FiUsers },
          { clave: 'menu.proyectos', path: '/dashboard/gestion-proyectos', icon: FiFolder },
          { clave: 'menu.servicios', path: '/dashboard/gestion-servicios', icon: FiPackage },
          { clave: 'menu.notificaciones', path: '/dashboard/notificaciones', icon: FiBell },
        ],
      },
      {
        id: 'material',
        clave: 'menu.seccionMaterial',
        items: [
          { clave: 'menu.equipos', path: '/dashboard/equipos', icon: FiMonitor },
          { clave: 'menu.perifericos', path: '/dashboard/perifericos', icon: FiHardDrive },
        ],
      },
      {
        id: 'sistema',
        clave: 'menu.seccionSistema',
        items: [
          { clave: 'menu.ajustes', path: '/dashboard/ajustes-del-sistema', icon: FiSettings },
        ],
      },
      miEspacio,
    ];
  }

  if (role === 'tecnico') {
    return [
      {
        id: 'general',
        clave: 'menu.seccionGeneral',
        items: [
          { clave: 'menu.panel', path: '/dashboard', icon: FiHome },
        ],
      },
      {
        id: 'gestion',
        clave: 'menu.seccionGestion',
        items: [
          { clave: 'menu.gestionTickets', path: '/dashboard/tickets', icon: FiInbox },
          { clave: 'menu.gestionUsuarios', path: '/dashboard/usuarios', icon: FiUsers },
        ],
      },
      miEspacio,
    ];
  }

  // Cliente
  return [
    {
      id: 'general',
      clave: 'menu.seccionGeneral',
      items: [
        { clave: 'menu.misTickets', path: '/dashboard', icon: FiInbox },
        { clave: 'menu.nuevoTicket', path: '/nuevo-ticket', icon: FiPlusCircle },
        { clave: 'menu.misProyectos', path: '/dashboard/proyectos', icon: FiGlobe },
      ],
    },
    miEspacio,
  ];
};

/**
 * Panel lateral (nav por rol + perfil + logout). Lo comparten la Sidebar de
 * las rutas de app y el Header público, así el menú lateral responsive es
 * idéntico en el inicio y en el dashboard.
 *
 * @param open               panel desplegado (móvil)
 * @param onClose            cierre tras navegar / logout
 * @param soloMovil          true en el Header público: el panel no existe en escritorio
 * @param conNotificaciones  campana en la cabecera del panel. Solo la usa la
 *                           sidebar fija de escritorio; en responsive la
 *                           campana vive en la barra superior / el header.
 */
const MenuLateral = ({ open = false, onClose = () => {}, soloMovil = false, conNotificaciones = false }) => {
  const [token, setToken] = useState(localStorage.getItem('AuthToken'));
  const tokenInfo = token ? jwtDecode(token) : {};
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const role = tokenInfo.role;
  const esCliente = role !== 'admin' && role !== 'tecnico';
  const esStaff = !esCliente;


  const secciones = buildNav(role);

  const displayName = [tokenInfo.nombre, tokenInfo.apellidos].filter(Boolean).join(' ') || tokenInfo.usuario || t('comun.usuario');

  useEffect(() => {
    const onTokenChanged = () => setToken(localStorage.getItem('AuthToken'));
    window.addEventListener('auth-token-changed', onTokenChanged);
    return () => window.removeEventListener('auth-token-changed', onTokenChanged);
  }, []);

  // Ficha del propio usuario (rol y departamento del pie del panel)
  const { data: usuarioInfo = null } = useQuery({
    queryKey: claves.usuario(tokenInfo.usuario),
    queryFn: async () => (await axios.get(`${BASE_URL}/usuario/info/${tokenInfo.usuario}`, cabecerasAuth())).data.usuario,
    enabled: Boolean(esStaff && tokenInfo.usuario),
  });

  // El contador de urgentes sin asignar sale de la misma caché de tickets que
  // usan el panel y el gestor: aquí solo se deriva el número con `select`.
  const { data: ticketsUrgentesSinAsignar = 0 } = useQuery({
    queryKey: claves.ticketsTodos(),
    queryFn: async () => (await axios.get(`${BASE_URL}/tickets/total/?page=1&limit=1000`, cabecerasAuth())).data.tickets || [],
    enabled: esStaff,
    select: (tickets) => tickets.filter((ticket) => (
      ticket.prioridad === 'urgente' && ticket.usuario_encargado === 'sin_asignar'
    )).length,
  });

  const isActive = (path) => location.pathname === path;

  const handleLinkClick = () => {
    window.scrollTo({ top: 0 });
    onClose();
  };

  const handleLogout = () => {
    localStorage.removeItem('AuthToken');
    navigate('/iniciar-sesion', { replace: true });
    window.location.reload();
  };

  const renderNavItem = (item) => {
    const alerta = item.path === '/dashboard/tickets' && ticketsUrgentesSinAsignar > 0;

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={handleLinkClick}
        className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
      >
        <span className="sidebar-link-icono"><item.icon /></span>
        <span className="sidebar-link-texto">{t(item.clave)}</span>
        {alerta && (
          <span className="sidebar-alert-badge" title={t('menu.ticketsUrgentesSinAsignar')}>{ticketsUrgentesSinAsignar}</span>
        )}
      </Link>
    );
  };

  return (
    <aside className={`app-sidebar ${open ? 'open' : ''} ${soloMovil ? 'solo-movil' : ''}`}>
      <div className="sidebar-header">
        <Link to="/" className="sidebar-logo-link" onClick={handleLinkClick}>
          <img className="sidebar-logo" src="/images/logoadhoc.svg" alt={t('comun.marcaLogo')} />
        </Link>
        {conNotificaciones && <Notificaciones />}
      </div>

      <nav className="sidebar-nav">
        {secciones.map((seccion) => (
          <div className="sidebar-seccion" key={seccion.id}>
            <p className="sidebar-seccion-titulo">{t(seccion.clave)}</p>
            {seccion.items.map(renderNavItem)}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <a
          href="https://agenciaadhoc.com/reportes"
          target="_blank"
          rel="noopener noreferrer"
          className="sidebar-link sidebar-reportar"
        >
          <span className="sidebar-link-icono"><FiAlertCircle /></span>
          <span className="sidebar-link-texto">{t('menu.reportarError')}</span>
        </a>

        <button
          type="button"
          className="sidebar-profile"
          onClick={() => { navigate('/dashboard/perfil'); handleLinkClick(); }}
          title={t('menu.miCuenta')}
        >
          <FotoPerfil editable={false} />
          <div className="sidebar-profile-info">
            <h3 className="sidebar-profile-name">{displayName}</h3>
            {esStaff && usuarioInfo ? (
              <p
                className="sidebar-profile-meta"
                title={`${usuarioInfo.role === 'admin' ? t('menu.administrador') : t('menu.tecnico')} · ${usuarioInfo.dpto_tecnico && DEPTO_LABELS_CORTO[usuarioInfo.dpto_tecnico] ? t(`usuarios.departamentosCorto.${usuarioInfo.dpto_tecnico}`) : t('menu.todosDptos')}`}
              >
                <span className={`sidebar-role-punto ${usuarioInfo.role === 'admin' ? 'admin' : 'tecnico'}`} aria-hidden="true" />
                <span className="sidebar-role-texto">
                  {usuarioInfo.role === 'admin' ? t('menu.administrador') : t('menu.tecnico')}
                </span>
                <span className="sidebar-meta-sep" aria-hidden="true">·</span>
                <span className="sidebar-dpto-texto">
                  {usuarioInfo.dpto_tecnico && DEPTO_LABELS_CORTO[usuarioInfo.dpto_tecnico]
                    ? t(`usuarios.departamentosCorto.${usuarioInfo.dpto_tecnico}`)
                    : t('menu.todosDptos')}
                </span>
              </p>
            ) : (
              tokenInfo.usuario && <p className="sidebar-profile-username">@{tokenInfo.usuario}</p>
            )}
          </div>
          <FiChevronRight className="sidebar-profile-chevron" />
        </button>

        <button type="button" className="sidebar-action-btn sidebar-logout" onClick={handleLogout}>
          <FiLogOut /> {t('menu.cerrarSesion')}
        </button>
      </div>
    </aside>
  );
};

export default MenuLateral;
